"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Calendar, ChevronRight, ChevronLeft, CircleCheck, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Progress } from "@/components/ui/progress";
const PACKAGE_OPTIONS = ["EDM / Electronic", "EDM Pop", "EDM Rock", "Country Pop", "Other / Experimental"];
export default function ArtistApplication() {
  return <section data-section-id="223774" id="artist-application" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/10 blur-[120px]" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-default font-bold mb-4 tracking-wider uppercase">
            Artist Application
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">
            Join the Heavens Horizons roster. Submit your demo and details below to start your journey beyond the horizon.
          </p>
        </div>

        <FormProvider formKey="artist_application_form_submissions" formId="artist_application_form_submissions" sectionName="Artist Application" defaultValues={{
        parent_name: "",
        parent_email: "",
        parent_phone: "",
        child_name: "",
        party_date: "",
        party_time: "",
        package: "",
        special_requests: ""
      }} validationRules={{
        parent_name: {
          required: true
        },
        parent_email: {
          required: true,
          pattern: "email"
        },
        parent_phone: {
          required: true,
          pattern: "phone"
        },
        child_name: {
          required: true
        },
        party_date: {
          required: true
        },
        party_time: {
          required: true
        },
        package: {
          required: true
        }
      }} submitText="Submit Application" successMessage="Your application has been transmitted. Our A&R Team will review your submission and reach out if there's a fit.">
          <BookingForm />
        </FormProvider>
      </div>
    </section>;
}
function BookingForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = () => setStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  const inputClasses = "flex h-12 w-full rounded-md border border-border bg-card/50 px-4 py-2 text-base shadow-md transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50";
  if (isSubmitted && !submitError) {
    return <Card className="bg-card/40 backdrop-blur-xl border-border shadow-2xl p-8 text-center animate-in fade-in zoom-in duration-500">
        <div className="flex justify-center mb-6">
          <div className="h-20 w-20 bg-primary/20 rounded-lg flex items-center justify-center">
            <CircleCheck className="h-10 w-10 text-primary" />
          </div>
        </div>
        <h3 className="text-2xl font-default font-bold mb-4">Submission Successful</h3>
        <p className="text-muted-foreground font-default mb-8 max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" size="lg" className="rounded-md border-primary text-primary hover:bg-primary/10">
          Submit Another Demo
        </Button>
      </Card>;
  }
  return <Card className="bg-card/30 backdrop-blur-2xl border-border shadow-4xl overflow-hidden">
      <div className="bg-muted/10 px-8 py-6 border-b border-border">
        <div className="flex justify-between items-center mb-4">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
            Phase {step} of {totalSteps}
          </span>
          <span className="text-xs font-bold text-primary uppercase tracking-widest">
            {step === 1 && "Artist Identity"}
            {step === 2 && "Release Timeline"}
            {step === 3 && "Genre & Vision"}
          </span>
        </div>
        <Progress value={progress} className="h-1 bg-muted" />
      </div>

      <CardContent className="p-8">
        <Form onSubmit={handleSubmit}>
          {submitError && <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm flex items-center gap-2">
              <span className="font-semibold">System Error:</span> {submitError}
            </div>}

          {}
          <div className={`space-y-6 ${step !== 1 ? 'hidden' : 'block animate-in slide-in-from-right-4 fade-in duration-300'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="parent_name" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-sm font-bold uppercase tracking-wider">Legal Name *</FormLabel>
                    <FormControl>
                      <input {...field} type="text" placeholder="John Doe" className={inputClasses} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="child_name" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-sm font-bold uppercase tracking-wider">Artist / Stage Name *</FormLabel>
                    <FormControl>
                      <input {...field} type="text" placeholder="Horizon Pulse" className={inputClasses} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField control={control} name="parent_email" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-sm font-bold uppercase tracking-wider">Contact Email *</FormLabel>
                    <FormControl>
                      <input {...field} type="email" placeholder="artist@horizon.com" className={inputClasses} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={control} name="parent_phone" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-sm font-bold uppercase tracking-wider">Phone Number *</FormLabel>
                    <FormControl>
                      <input {...field} type="tel" placeholder="+1 (555) 000-0000" className={inputClasses} />
                    </FormControl>
                    <FormDescription className="text-xs">For urgent A&R communications.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {}
          <div className={`space-y-6 ${step !== 2 ? 'hidden' : 'block animate-in slide-in-from-right-4 fade-in duration-300'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FormField control={control} name="party_date" render={({
              field
            }) => <FormItem className="flex flex-col">
                    <FormLabel className="text-sm font-bold uppercase tracking-wider">Target Release Date *</FormLabel>
                    <div className="rounded-lg border border-border bg-card/50 p-3 shadow-md flex justify-center">
                      <SafeHydrate><CalendarComponent mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => {
                    if (date) {
                      const offset = date.getTimezoneOffset();
                      const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
                      field.onChange(adjustedDate.toISOString().split('T')[0]);
                    } else {
                      field.onChange("");
                    }
                  }} disabled={date => date < new Date()} className="pointer-events-auto" /></SafeHydrate>
                    </div>
                    <FormDescription className="text-xs">When do you envision this track dropping?</FormDescription>
                    <FormMessage />
                  </FormItem>} />
              
              <div className="space-y-6">
                <FormField control={control} name="party_time" render={({
                field
              }) => <FormItem>
                      <FormLabel className="text-sm font-bold uppercase tracking-wider">Demo Link (SoundCloud/Drive) *</FormLabel>
                      <FormControl>
                        <input {...field} type="text" placeholder="https://soundcloud.com/..." className={inputClasses} />
                      </FormControl>
                      <FormDescription className="text-xs">Ensure the link is public or has permissions.</FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>
            </div>
          </div>

          {}
          <div className={`space-y-6 ${step !== 3 ? 'hidden' : 'block animate-in slide-in-from-right-4 fade-in duration-300'}`}>
            <FormField control={control} name="package" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-bold uppercase tracking-wider">Primary Genre *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-14 border border-border bg-card/50 text-base px-4 rounded-md focus:ring-primary/30">
                        <SelectValue placeholder="Select your sound" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-card border-border">
                      {PACKAGE_OPTIONS.map((pkg, i) => <SelectItem key={i} value={pkg} data-index={i} className="py-3 text-base focus:bg-primary/20">
                          {pkg}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="special_requests" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-sm font-bold uppercase tracking-wider">Artist Bio / Vision</FormLabel>
                  <FormControl>
                    <textarea {...field} placeholder="Tell us about your musical journey and what you hope to achieve with Heavens Horizons." className={`${inputClasses} min-h-[120px] resize-y py-3 border-dashed border-2 hover:border-secondary transition-colors`} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <div className="mt-10 pt-6 border-t border-border flex items-center justify-between">
            <Button type="button" variant="outline" size="lg" onClick={prevStep} disabled={step === 1 || isSubmitting} className="w-32 rounded-md border-border hover:bg-muted">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            {step < totalSteps ? <Button type="button" size="lg" onClick={nextStep} className="w-32 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md shadow-lg shadow-primary/20">
                Next
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button> : <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-64 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground rounded-md shadow-xl shadow-primary/20 font-bold tracking-widest uppercase">
                {isSubmitting ? <>
                    <Loader className="mr-2 h-5 w-5 animate-spin" />
                    Transmitting...
                  </> : "Submit Application"}
              </Button>}
          </div>
        </Form>
      </CardContent>
    </Card>;
}